import { trackEvent } from '@helpers/analytics';
import { Dialog, Typography, TypographyType } from '@vivino/js-react-common-ui';
import {
  BasicVintage,
  Loader,
  Price,
  ValueForMoneyScoresFull,
  getUniqueTrackingId,
  localStorage,
  t,
} from '@vivino/js-web-common';
import React, { useState } from 'react';

import SentimentTracker, { Sentiment } from '@components/SentimentTracker/SentimentTracker';
import VFMGauge from '@components/icons/VFMGauge';

import { scaleToRotation } from '../../helpers';
import RatingScale from '../RatingScale/RatingScale';
import VFMBadge from '../VFMBadge/VFMBadge';
import styles from './explainer.module.scss';

const TRANSLATIONS = {
  thanks: 'common.thanks',
  tellUsMore: 'common.tell_us_more',
  learnMore: 'common.learn_more',
  yesOtherLabels: 'components.shared.vfm_scale.yes_there_are',
  otherLabels: 'components.shared.vfm_scale.other_value_labels',
  calculationText: 'components.shared.vfm_scale.calculation_text',
  thisWine: 'components.shared.vfm_scale.this_wine',
  thisWinesRating: 'components.shared.vfm_scale.this_wines_rating',
  otherWinesRating: 'components.shared.vfm_scale.average_rating_for_similar_wines',
};

interface Props {
  open: boolean;
  onClose: () => void;
  qprScores?: ValueForMoneyScoresFull;
  vintage: BasicVintage;
  price: Price;
  trackingProps?: any;
}

const EXPLAINER_SURVEY_FORM_ID = '01HXBNATJKD8WCGB2X62R1373Z';

const Explainer = ({ open, onClose, qprScores, trackingProps, vintage, price }: Props) => {
  const priceId = price.id;
  const vintageId = vintage.id;
  const userId = getUniqueTrackingId();

  const sentimentInfoKey = `vfm_sentiment_info${vintageId}_${priceId}_${userId}`;
  const sentimentInfo = localStorage.getItem(sentimentInfoKey) as Sentiment;

  const [sentimentState, setSentimentState] = useState(sentimentInfo);
  const [isLoading, setIsLoading] = useState(false);

  function handleSentimentChange(sentiment) {
    trackEvent({
      event: 'Value explainer - Screen - Action',
      props: {
        ...trackingProps,
        Action: `${sentiment} sentiment`,
      },
    });
    setSentimentState(sentiment);
    localStorage.setItem(sentimentInfoKey, sentiment);
  }

  function injectTypeformScript() {
    setIsLoading(true);
    trackEvent({
      event: 'Value explainer - Screen - Action',
      props: {
        ...trackingProps,
        Action: 'Open survey',
      },
    });

    const container = document.getElementById('typeform-container');
    if (container) {
      const embedElement = document.createElement('script');
      embedElement.src = '//embed.typeform.com/next/embed.js';
      embedElement.onload = function () {
        // setIsLoading(false);
        const formId = EXPLAINER_SURVEY_FORM_ID;
        const options = {
          container: container,
          url: `https://form.typeform.com/to/${formId}`,
        };
        window.tf.createWidget(container, options.url, options);
      };
      document.body.appendChild(embedElement);
    }
  }

  function handleClose() {
    onClose?.();
    setIsLoading(false);
  }

  function getSentimentTitle(sentiment: Sentiment) {
    if (!sentiment) {
      return null;
    }
    if (sentiment === Sentiment.POSITIVE) {
      return t(TRANSLATIONS.thanks);
    }
    return (
      <span>
        {t(TRANSLATIONS.thanks)}{' '}
        <a data-testid="tell-me-more" onClick={injectTypeformScript}>
          {t(TRANSLATIONS.tellUsMore)}
        </a>
      </span>
    );
  }

  function colorizeValueCategoryExplainer(text: string, categoryId: number) {
    const categoryColorCode = {
      1: '#800F1C',
      2: '#986801',
      3: '#006B4D',
      4: '#006B4D',
      5: '#006B4D',
      6: '#006B4D',
    };

    const regex = /\*([^*]+)\*/gi;
    const segments = text.split(regex);

    const processed = segments.map((segment, index) => {
      if (index % 2 === 1) {
        const color = categoryColorCode[categoryId];
        return (
          <span key={index} data-key={index} style={{ fontWeight: 500, color }}>
            {segment}
          </span>
        );
      } else {
        return (
          <span data-key={index} key={index}>
            {segment}
          </span>
        );
      }
    });

    return processed;
  }

  const sortedCategories = qprScores?.possible_value_categories.sort((a, b) => b.id - a.id) || [];
  let categoryId = qprScores?.value_category?.id;
  const unAvailable = categoryId === 0;

  if (categoryId === 4) {
    categoryId = 3; // 4 is the same as 3
  }

  if (!qprScores) {
    return null;
  }

  return (
    <div data-testid="typeform-explainer" className={styles.container}>
      <Dialog dismissable open={open} onClose={handleClose}>
        <div className={styles.explainer}>
          <Typography type={TypographyType.BodyMedium}>
            {colorizeValueCategoryExplainer(qprScores?.value_category_explainer, categoryId)}
          </Typography>
          {!unAvailable && (
            <>
              <RatingScale qprScores={qprScores} />
              <SentimentTracker
                title={getSentimentTitle(sentimentState)}
                sentiment={sentimentState}
                onClick={handleSentimentChange}
              />
            </>
          )}
          <div
            id="typeform-container"
            data-testid="typeform-explainer-survey"
            className={styles.explainerSurvey}
            data-tf-live={EXPLAINER_SURVEY_FORM_ID}
            data-tf-inline-on-mobile="true"
            data-tf-hide-footer="true"
            {...(isLoading && { style: { display: 'block' } })}
          >
            <Loader />
          </div>

          <div className={styles.calculationText}>
            {t(TRANSLATIONS.calculationText, {
              vintage_count: (
                <span key={0} className={styles.vintageCount}>
                  {unAvailable ? '10+' : qprScores?.peer_group?.statistics?.vintage_count}
                </span>
              ),
            })}
            <span>.</span>
          </div>
          <div className={styles.divider} />
          <div>
            <Typography type={TypographyType.LabelLarge}>{t(TRANSLATIONS.otherLabels)}</Typography>
            <Typography type={TypographyType.BodySmall}>
              {t(TRANSLATIONS.yesOtherLabels)}
            </Typography>
          </div>
          <div className={styles.categories}>
            {sortedCategories.map((category) => (
              <div key={category.id} className={styles.category}>
                <div className={styles.gauge}>
                  <VFMGauge
                    rotation={scaleToRotation(category.barometer_position)}
                    unAvailable={category.id === 0}
                  />
                </div>
                <div className={styles.text}>
                  <div className={styles.thisWine}>
                    <VFMBadge text={category.title} position={category.barometer_position} />
                    {category.id === categoryId && (
                      <Typography type={TypographyType.LabelSmall}>
                        {t(TRANSLATIONS.thisWine)}
                      </Typography>
                    )}
                  </div>
                  <Typography type={TypographyType.BodySmall}>{category.subtitle}</Typography>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Explainer;
